import React, { useState, useMemo, useCallback } from 'react';
import { Container, Row, Col, Button, Card, Form, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaNetworkWired, 
  FaArrowRight, 
  FaCheckCircle,
  FaLightbulb,
  FaRocket,
  FaFilter,
  FaSearch,
  FaTh,
  FaList,
  FaCog,
  FaEthernet,
  FaWifi,
  FaShieldAlt,
  FaCloud,
  FaServer,
  FaPhone,
  FaExternalLinkAlt,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import { useScrollAnimation } from '../../../hooks';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Helmet } from 'react-helmet-async';

const AccessSwitching = () => {
  const { t, lang } = useLanguage();

  // Données bilingues intégrées pour les détails des produits
  const SWITCH_DATA = useMemo(() => ({
    categories: [
      {
        id: 'catalyst-9000',
        name: 'Cisco Catalyst 9000 Series',
        description: lang === 'fr' ? 'Switches enterprise-class pour réseaux campus modernes' : 'Enterprise-class switches for modern campus networks',
        icon: <FaNetworkWired />
      },
      {
        id: 'smart-switches',
        name: 'Smart Switches',
        description: lang === 'fr' ? 'Switches intelligents nouvelle génération' : 'Next-generation smart switches',
        icon: <FaRocket />
      },
      {
        id: 'meraki-cloud',
        name: 'Cisco Meraki',
        description: lang === 'fr' ? 'Switches cloud-managed pour simplification réseau' : 'Cloud-managed switches for network simplification',
        icon: <FaCloud />
      },
      {
        id: 'small-business',
        name: 'Small Business',
        description: lang === 'fr' ? 'Solutions switching pour PME et petites branches' : 'Switching solutions for SMBs and small branches',
        icon: <FaServer />
      }
    ],

    switches: [
      {
        id: 1,
        name: 'Cisco Catalyst 9400 Series',
        category: 'catalyst-9000',
        switchType: 'modular',
        isNew: false,
        image: '/images/AccessSwitch/cisco-cat9400.png',
        specs: {
          ports: 'Up to 384 x 1/10/25G ports',
          uplinks: '4 x 40/100G uplinks',
          backplane: 'Up to 11.52 Tbps',
          power: 'Up to 6000W PoE+/UPoE+',
          rackUnits: '7U/10U'
        },
        workloads: lang === 'fr' ? ['Cœur de campus d\'entreprise', 'Agrégation de grandes succursales', 'Accès data center', 'Accès haute densité'] : ['Enterprise campus core', 'Large branch aggregation', 'Data center access', 'High-density access'],
        features: lang === 'fr' ? ['Conception modulaire', 'Sécurité avancée', 'Prêt pour AI/ML', 'Réseau basé sur l\'intention'] : ['Modular design', 'Advanced security', 'AI/ML ready', 'Intent-based networking'],
        badge: 'Enterprise'
      },
      {
        id: 2,
        name: 'Cisco Catalyst 9300 Series',
        category: 'catalyst-9000',
        isNew: false,
        image: '/images/AccessSwitch/cisco-cat9300.png',
        specs: {
          ports: '12-48 x 1/10/25G ports',
          uplinks: '4 x 1/10/25G uplinks',
          backplane: 'Up to 440 Gbps',
          power: 'Up to 1440W PoE+',
          rackUnits: '1U'
        },
        workloads: lang === 'fr' ? ['Accès campus', 'Agrégation de succursales', 'Connectivité IoT', 'Points d\'accès sans fil'] : ['Campus access', 'Branch aggregation', 'IoT connectivity', 'Wireless access points'],
        features: lang === 'fr' ? ['Empilable jusqu\'à 8 unités', 'Automatisation DNA', 'Chiffrement MACsec', 'Sécurité IoT'] : ['Stackable up to 8 units', 'DNA automation', 'MACsec encryption', 'IoT security'],
      },
      {
        id: 3,
        name: 'Cisco Catalyst 9200 Series',
        category: 'catalyst-9000',
        switchType: 'stackable',
        isNew: false,
        image: '/images/AccessSwitch/cisco-cat9200.png',
        specs: {
          ports: '24-48 x 1G ports',
          uplinks: '4 x 1/10G SFP+ uplinks',
          backplane: 'Up to 176 Gbps',
          power: 'Up to 740W PoE+',
          rackUnits: '1U'
        },
        workloads: lang === 'fr' ? ['Petit accès campus', 'Connectivité succursale', 'Réseaux PME', 'Commutation de base'] : ['Small campus access', 'Branch connectivity', 'SMB networks', 'Basic switching'],
        features: lang === 'fr' ? ['Rentable', 'Efficacité énergétique', 'Gestion facile', 'Prêt pour Cisco DNA'] : ['Cost-effective', 'Energy efficient', 'Easy management', 'Cisco DNA ready'],
        badge: lang === 'fr' ? 'Économique' : 'Value'
      },
      {
        id: 4,
        name: 'Cisco Catalyst 9300X Series',
        category: 'catalyst-9000',
        switchType: 'stackable',
        isNew: true,
        image: '/images/AccessSwitch/cisco-cat9300x.png',
        specs: {
          ports: '12-48 x 1/10/25G ports',
          uplinks: '8 x 1/10/25G uplinks',
          backplane: 'Up to 880 Gbps',
          power: 'Up to 1440W PoE++',
          rackUnits: '1U'
        },
        workloads: lang === 'fr' ? ['Accès haute performance', 'Support WiFi 6/6E', 'Déploiements IoT', 'Streaming vidéo'] : ['High-performance access', 'WiFi 6/6E support', 'IoT deployments', 'Video streaming'],
        features: lang === 'fr' ? ['Performances améliorées', 'Prêt pour WiFi 6E', 'PoE++ avancé', 'Optimisation IA'] : ['Enhanced performance', 'WiFi 6E ready', 'Advanced PoE++', 'AI optimization'],
        badge: lang === 'fr' ? 'Nouveau' : 'New'
      },
      {
        id: 5,
        name: 'Cisco C9350 Series Smart Switches',
        category: 'smart-switches',
        switchType: 'smart',
        isNew: true,
        image: '/images/AccessSwitch/cisco-c9350.png',
        specs: {
          ports: '12-48 x 1/10/25G ports',
          uplinks: '4 x 1/10/25G uplinks',
          backplane: 'Up to 440 Gbps',
          power: 'Up to 1440W PoE++',
          rackUnits: '1U'
        },
        workloads: lang === 'fr' ? ['Charges de travail IA', 'Bâtiment intelligent', 'Sécurité IoT', 'Automatisation réseau'] : ['AI workloads', 'Smart building', 'IoT security', 'Network automation'],
        features: lang === 'fr' ? ['Propulsé par l\'IA', 'Sécurité intégrée', 'Déploiement zero-touch', 'Efficacité énergétique'] : ['AI-powered', 'Built-in security', 'Zero-touch deployment', 'Energy efficient'],
        badge: 'Intelligence'
      },
      {
        id: 6,
        name: 'Cisco Meraki MS390 Series',
        category: 'meraki-cloud',
        switchType: 'cloud-managed',
        isNew: false,
        image: '/images/AccessSwitch/cisco-meraki-ms390.png',
        specs: {
          ports: '24-48 x 1/10G ports',
          uplinks: '4 x 10G SFP+ uplinks',
          backplane: 'Up to 440 Gbps',
          power: 'Up to 740W PoE+',
          rackUnits: '1U'
        },
        workloads: lang === 'fr' ? ['Agrégation de campus', 'Connectivité succursale', 'Gestion cloud', 'Intégration SD-WAN'] : ['Campus aggregation', 'Branch connectivity', 'Cloud management', 'SD-WAN integration'],
        features: lang === 'fr' ? ['Tableau de bord Cloud', 'Déploiement zero-touch', 'Analytique réseau', 'Gestion simplifiée'] : ['Cloud dashboard', 'Zero-touch deployment', 'Network analytics', 'Simplified management'],
        badge: 'Cloud'
      },
      {
        id: 7,
        name: 'Cisco Meraki MS225 Series',
        category: 'meraki-cloud',
        switchType: 'cloud-managed',
        isNew: false,
        image: '/images/AccessSwitch/cisco-meraki-ms225.png',
        specs: {
          ports: '8-48 x 1G ports',
          uplinks: '4 x 1/10G SFP+ uplinks',
          backplane: 'Up to 176 Gbps',
          power: 'Up to 370W PoE+',
          rackUnits: '1U'
        },
        workloads: lang === 'fr' ? ['Couche d\'accès', 'Petite succursale', 'Support AP sans fil', 'Connectivité IoT'] : ['Access layer', 'Small branch', 'Wireless AP support', 'IoT connectivity'],
        features: lang === 'fr' ? ['Gestion cloud simple', 'Efficacité énergétique', 'Design compact', 'Plug-and-play'] : ['Simple cloud management', 'Energy efficient', 'Compact design', 'Plug-and-play'],
        badge: 'Simple'
      },
      {
        id: 8,
        name: 'Cisco Catalyst 1300 Series',
        category: 'small-business',
        switchType: 'unmanaged',
        isNew: false,
        image: '/images/AccessSwitch/cisco-cat1300.png',
        specs: {
          ports: '8-48 x 1G ports',
          uplinks: '2-4 x 1G SFP uplinks',
          backplane: 'Up to 100 Gbps',
          power: 'Up to 185W PoE+',
          rackUnits: '1U'
        },
        workloads: lang === 'fr' ? ['Petit bureau', 'Bureau à domicile', 'Réseautage de base', 'Systèmes de surveillance'] : ['Small office', 'Home office', 'Basic networking', 'Surveillance systems'],
        features: lang === 'fr' ? ['Plug-and-play', 'Efficacité énergétique', 'Design compact', 'Fonctionnement sans ventilateur'] : ['Plug-and-play', 'Energy efficient', 'Compact design', 'Fanless operation'],
        badge: lang === 'fr' ? 'Basique' : 'Basic'
      },
      {
        id: 9,
        name: 'Cisco Business 350 Series',
        category: 'small-business',
        switchType: 'managed',
        isNew: false,
        image: '/images/AccessSwitch/cisco-business350.png',
        specs: {
          ports: '8-52 x 1G ports',
          uplinks: '2-4 x 10G SFP+ uplinks',
          backplane: 'Up to 128 Gbps',
          power: 'Up to 382W PoE+',
          rackUnits: '1U'
        },
        workloads: lang === 'fr' ? ['Réseaux PME', 'Bureaux de succursale', 'Systèmes VoIP', 'Vidéosurveillance'] : ['SMB networks', 'Branch offices', 'VoIP systems', 'Video surveillance'],
        features: lang === 'fr' ? ['Gestion via le Web', 'Support VLAN', 'Fonctionnalités QoS', 'Politiques de sécurité'] : ['Web-based management', 'VLAN support', 'QoS features', 'Security policies'],
        badge: lang === 'fr' ? 'Gestionnable' : 'Managed'
      },
      {
        id: 10,
        name: 'Cisco Catalyst 9300L Series',
        category: 'catalyst-9000',
        switchType: 'fixed',
        isNew: false,
        image: '/images/AccessSwitch/cisco-cat9300l.png',
        specs: {
          ports: '24-48 x 1G ports',
          uplinks: '4 x 1/10G SFP+ uplinks',
          backplane: 'Up to 176 Gbps',
          power: 'Up to 740W PoE+',
          rackUnits: '1U'
        },
        workloads: lang === 'fr' ? ['Déploiements soucieux des coûts', 'Accès entreprise de base', 'Établissements d\'enseignement', 'Gouvernement'] : ['Cost-conscious deployments', 'Basic enterprise access', 'Educational institutions', 'Government'],
        features: lang === 'fr' ? ['Cisco DNA Essentials', 'Sécurité de base', 'Certifié Energy Star', 'Licences simplifiées'] : ['Cisco DNA Essentials', 'Basic security', 'Energy Star certified', 'Simplified licensing'],
        badge: lang === 'fr' ? 'Essentiel' : 'Essential'
      }
    ]
  }), [lang]);

  // Hooks animations
  const [setHeroRef, heroInView] = useScrollAnimation(0.1);
  const [setCategoriesRef, categoriesInView] = useScrollAnimation(0.1);
  const [setSwitchesRef, switchesInView] = useScrollAnimation(0.1);

  // État filtres
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = viewMode === 'grid' ? 3 : 5;

  const filteredSwitches = useMemo(() => {
    let result = SWITCH_DATA.switches;
    if (selectedCategory !== 'all') result = result.filter(sw => sw.category === selectedCategory);
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(sw =>
        sw.name.toLowerCase().includes(search) ||
        sw.workloads.some(w => w.toLowerCase().includes(search)) ||
        sw.features.some(f => f.toLowerCase().includes(search))
      );
    }
    return result;
  }, [selectedCategory, searchTerm, SWITCH_DATA]);

  const totalPages = Math.ceil(filteredSwitches.length / itemsPerPage);
  const paginatedSwitches = filteredSwitches.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleCategoryChange = (category) => { setSelectedCategory(category); setCurrentPage(1); };
  const handleSearchChange = (e) => { setSearchTerm(e.target.value); setCurrentPage(1); };
  const handleViewModeChange = (mode) => { setViewMode(mode); setCurrentPage(1); };
  const scrollToSwitches = () => { document.getElementById('switches-grid')?.scrollIntoView({ behavior: 'smooth' }); };

  return (
    <div>

      {/* --- SEO DYNAMIQUE POUR GOOGLE --- */}
      <Helmet>
        <title>{t.accessSwitching.seo.title}</title>
        <meta name="description" content={t.accessSwitching.seo.description} />
        <link rel="canonical" href="https://www.s2i.com.tn/solutions/access-switching" />
        
        {/* Open Graph pour LinkedIn / Facebook */}
        <meta property="og:title" content={t.accessSwitching.seo.title} />
        <meta property="og:description" content={t.accessSwitching.seo.description} />
        <meta property="og:image" content="https://www.s2i.com.tn/images/AccessSwitch/cisco-cat9300.png" />
        <meta property="og:type" content="product" />
      </Helmet>
      {/* ---------------------------------- */}

      {/* Hero Section */}
      <section 
        ref={setHeroRef}
        className="hero-section-sii" 
        style={{ 
          background: `linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(71, 85, 105, 0.9) 100%), url('/images/AccessSwitch/cisco-campus-switching1  .jpg')`,
          backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed', minHeight: '80vh', display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden'
        }}
      >
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")', opacity: 0.3 }} />
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <Row className="align-items-center">
            <Col lg={6}>
              <motion.div initial={{ opacity: 0, y: 50 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
                <div className="mb-4"><span className="badge-sii"><FaNetworkWired className="me-2" />{t.accessSwitching.hero.badge}</span></div>
                <h1 dangerouslySetInnerHTML={{ __html: t.accessSwitching.hero.title }} style={{ color: '#fff' }} className="hero-title-sii" />
                <p style={{ color: 'rgba(255,255,255,0.9)' }} className="hero-subtitle-sii">{t.accessSwitching.hero.subtitle}</p>
                <div className="mt-4">
                  <Button onClick={scrollToSwitches} className="btn-sii-primary me-3" size="lg">{t.accessSwitching.hero.btnExplore} <FaArrowRight className="ms-2" /></Button>
                  <Button variant="outline-light" size="lg" onClick={() => window.location.href = '/contact'}>{t.accessSwitching.hero.btnQuote}</Button>
                </div>
              </motion.div>
            </Col>
            <Col lg={6} className="text-center">
              <motion.img initial={{ opacity: 0, x: 50 }} animate={heroInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, delay: 0.4 }} src="/images/AccessSwitch/cisco-campus-switching.jpg" style={{ width: '100%', maxWidth: 500, borderRadius: 16, boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }} />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Catégories */}
      <section ref={setCategoriesRef} className="solutions-section-sii" style={{ paddingBottom: 0 }}>
        <Container>
          <Col lg={12} className="text-center mb-5">
            <h2 className="section-title-sii">{t.accessSwitching.categories.title}</h2>
            <p className="section-subtitle-sii">{t.accessSwitching.categories.subtitle}</p>
          </Col>
          <Row className="g-4 mb-5">
            {SWITCH_DATA.categories.map((cat) => (
              <Col lg={3} md={6} key={cat.id}>
                <motion.div whileHover={{ y: -10 }} onClick={() => handleCategoryChange(cat.id)} style={{ background: '#fff', borderRadius: 16, padding: '2rem 1.5rem', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', cursor: 'pointer', transition: 'all 0.3s ease', border: '2px solid transparent' }}>
                  <div style={{ fontSize: '2.5rem', color: '#d90000', marginBottom: '1rem' }}>{cat.icon}</div>
                  <h4 style={{ fontWeight: 700 }}>{cat.name}</h4>
                  <p style={{ color: '#666', fontSize: '0.95rem' }}>{cat.description}</p>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>

        <Container fluid style={{ paddingBottom: '2rem', marginBottom: '4rem' }}>
          <div id="switches-grid" ref={setSwitchesRef}>
            <Row>
              {/* Sidebar Sidebar Filtres */}
              <Col lg={3}>
                <div style={{ position: 'sticky', top: '100px', background: '#fff', borderRadius: '20px', padding: '2rem', boxShadow: '0 8px 32px rgba(0,0,0,0.1)', border: '1px solid #e9ecef', height: 'fit-content' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem', paddingBottom: '1rem', borderBottom: '2px solid #e9ecef' }}>
                    <FaFilter color="#d90000" className="me-2" />
                    <h5 className="m-0 fw-bold">{t.accessSwitching.sidebar.title}</h5>
                  </div>
                  
                  <div className="mb-4">
                    <label className="small fw-bold text-uppercase mb-2 d-block">{t.accessSwitching.sidebar.search}</label>
                    <div className="position-relative">
                      <FaSearch style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                      <Form.Control type="text" placeholder={t.accessSwitching.sidebar.searchPlaceholder} value={searchTerm} onChange={handleSearchChange} style={{ borderRadius: '12px', paddingLeft: '2.5rem', background: '#f8f9fa' }} />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="small fw-bold text-uppercase mb-2 d-block">{t.accessSwitching.sidebar.category}</label>
                    <div className="d-grid gap-2">
                      <Button variant={selectedCategory === 'all' ? 'danger' : 'outline-secondary'} onClick={() => handleCategoryChange('all')} style={{ borderRadius: '12px', textAlign: 'left', border: '1px solid #dee2e6' }}>
                         <FaNetworkWired className="me-2" /> {t.accessSwitching.sidebar.all} ({SWITCH_DATA.switches.length})
                      </Button>
                      {SWITCH_DATA.categories.map(c => (
                        <Button key={c.id} variant={selectedCategory === c.id ? 'danger' : 'outline-secondary'} onClick={() => handleCategoryChange(c.id)} style={{ borderRadius: '12px', textAlign: 'left', border: '1px solid #dee2e6' }}>
                           <span className="me-2">{c.icon}</span>{c.name}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="p-3 text-center mb-4" style={{ background: '#f8f9fa', borderRadius: '12px', border: '1px solid #dee2e6' }}>
                    <p className="m-0 fw-bold"><span style={{ color: '#d90000' }}>{filteredSwitches.length}</span> {t.accessSwitching.sidebar.found}</p>
                    {totalPages > 1 && <p className="small text-muted m-0">Page {currentPage} {t.accessSwitching.sidebar.of} {totalPages}</p>}
                  </div>

                  <div className="mb-2">
                    <label className="small fw-bold text-uppercase mb-2 d-block">{t.accessSwitching.sidebar.view}</label>
                    <div className="btn-group d-flex" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                      <Button variant={viewMode === 'grid' ? 'danger' : 'outline-secondary'} onClick={() => handleViewModeChange('grid')} style={{ flex: 1, border: 'none' }}><FaTh className="me-2" />{t.accessSwitching.sidebar.grid}</Button>
                      <Button variant={viewMode === 'list' ? 'danger' : 'outline-secondary'} onClick={() => handleViewModeChange('list')} style={{ flex: 1, border: 'none' }}><FaList className="me-2" />{t.accessSwitching.sidebar.list}</Button>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Contenu Switches */}
              <Col lg={9}>
                {viewMode === 'grid' ? (
                  <Row className="g-4">
                    {paginatedSwitches.map((sw, idx) => (
                      <Col lg={4} md={6} key={sw.id}>
                        <motion.div initial={{ opacity: 0, y: 50 }} animate={switchesInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: idx * 0.1 }} whileHover={{ y: -10 }}>
                          <Card style={{ borderRadius: '20px', border: 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', height: '100%', overflow: 'hidden' }}>
                            <div style={{ height: '180px', background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                               <div style={{ position: 'absolute', top: '1rem', left: '1rem', right: '1rem', display: 'flex', justifyContent: 'space-between' }}>
                                 {sw.isNew && <Badge bg="success" style={{ fontSize: '0.7rem', padding: '0.4rem 0.8rem', borderRadius: '15px' }}>{lang === 'fr' ? 'NOUVEAU' : 'NEW'}</Badge>}
                                 {sw.badge && <Badge bg="danger" style={{ fontSize: '0.7rem', padding: '0.4rem 0.8rem', borderRadius: '15px' }}>{sw.badge}</Badge>}
                               </div>
                               <img src={sw.image} alt={sw.name} style={{ width: '90%', height: '90%', objectFit: 'contain' }} />
                               <div style={{ position: 'absolute', bottom: '1rem', left: '1rem', right: '1rem', display: 'flex', justifyContent: 'space-between' }}>
                                 {sw.switchType && <Badge bg="primary" style={{ fontSize: '0.65rem', borderRadius: '12px' }}>{sw.switchType}</Badge>}
                                 {sw.specs.rackUnits && <Badge bg="secondary" style={{ fontSize: '0.65rem', borderRadius: '12px' }}>{sw.specs.rackUnits}</Badge>}
                               </div>
                            </div>
                            <Card.Body style={{ padding: '1.75rem' }}>
                              <h5 className="fw-bold mb-3">{sw.name}</h5>
                              <div className="mb-4">
                                <small className="text-uppercase fw-bold text-muted d-block mb-2">{t.accessSwitching.card.usage}</small>
                                <div className="d-flex flex-wrap gap-1">
                                  {sw.workloads.slice(0, 3).map((w, i) => <Badge key={i} bg="light" text="dark" className="border" style={{ fontSize: '0.7rem' }}>{w}</Badge>)}
                                </div>
                              </div>
                              <div className="mb-4">
                                <small className="text-uppercase fw-bold text-muted d-block mb-2">{t.accessSwitching.card.specs}</small>
                                <div className="d-flex align-items-start mb-2"><div style={{ background: '#f7fafc', padding: '0.4rem', borderRadius: '8px', marginRight: '0.75rem' }}><FaNetworkWired size={14} /></div><div style={{ fontSize: '0.8rem' }}><strong>Ports:</strong> {sw.specs.ports}</div></div>
                                <div className="d-flex align-items-start mb-2"><div style={{ background: '#f7fafc', padding: '0.4rem', borderRadius: '8px', marginRight: '0.75rem' }}><FaEthernet size={14} /></div><div style={{ fontSize: '0.8rem' }}><strong>Uplinks:</strong> {sw.specs.uplinks}</div></div>
                                {sw.specs.power && <div className="d-flex align-items-start"><div style={{ background: '#f7fafc', padding: '0.4rem', borderRadius: '8px', marginRight: '0.75rem' }}><FaCog size={14} /></div><div style={{ fontSize: '0.8rem' }}><strong>PoE:</strong> {sw.specs.power}</div></div>}
                              </div>
                              <div>
                                <small className="text-uppercase fw-bold text-muted d-block mb-2">{t.accessSwitching.card.highlights}</small>
                                {sw.features.slice(0, 3).map((f, i) => <div key={i} className="small d-flex align-items-center mb-1"><FaCheckCircle color="#38a169" size={12} className="me-2" />{f}</div>)}
                              </div>
                            </Card.Body>
                          </Card>
                        </motion.div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div>
                    {paginatedSwitches.map((sw) => (
                      <motion.div key={sw.id} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} className="mb-4">
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                          <Card.Body className="p-4">
                            <Row className="align-items-center">
                              <Col md={2} className="text-center"><img src={sw.image} alt={sw.name} style={{ width: '100%', maxWidth: '100px', objectFit: 'contain' }} /></Col>
                              <Col md={4}><h5 className="fw-bold m-0">{sw.name}</h5><div className="mt-2"><Badge bg="danger" className="me-2">{sw.badge || 'Enterprise'}</Badge><Badge bg="primary">{sw.switchType}</Badge></div></Col>
                              <Col md={6} style={{ background: '#f8f9fa', padding: '1.2rem', borderRadius: '16px' }}>
                                <div className="small mb-1"><strong>Ports:</strong> {sw.specs.ports}</div>
                                <div className="small mb-1"><strong>Uplinks:</strong> {sw.specs.uplinks}</div>
                                <div className="small"><strong>Backplane:</strong> {sw.specs.backplane}</div>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* PAGINATION - CORRECTIF CLICKABLE */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center align-items-center mt-5 mb-4" style={{ position: 'relative', zIndex: 10 }}>
                    <div className="d-flex align-items-center" style={{ pointerEvents: 'auto' }}>
                      <Button 
                        variant="outline-primary" 
                        disabled={currentPage === 1} 
                        onClick={() => setCurrentPage(prev => prev - 1)} 
                        style={{ borderRadius: '50%', width: '40px', height: '40px', marginRight: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <FaChevronLeft />
                      </Button>
                      
                      <div className="d-flex gap-2">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                          <Button 
                            key={p} 
                            variant={currentPage === p ? 'danger' : 'outline-secondary'} 
                            onClick={() => setCurrentPage(p)} 
                            style={{ borderRadius: '50%', width: '40px', height: '40px', fontWeight: 600, border: currentPage === p ? 'none' : '1px solid #dee2e6' }}
                          >
                            {p}
                          </Button>
                        ))}
                      </div>

                      <Button 
                        variant="outline-primary" 
                        disabled={currentPage === totalPages} 
                        onClick={() => setCurrentPage(prev => prev + 1)} 
                        style={{ borderRadius: '50%', width: '40px', height: '40px', marginLeft: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <FaChevronRight />
                      </Button>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      {/* Other Solutions Section */}
      <section style={{ padding: '8rem 0', background: `linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(71, 85, 105, 0.95) 100%), url('/images/AccessSwitch/cisco-campus-switching1.jpg')`, backgroundSize: 'cover', backgroundAttachment: 'fixed', position: 'relative' }}>
         <Container style={{ position: 'relative', zIndex: 1 }}>
            <Col lg={12} className="text-center mb-5">
              <h2 className="section-title-sii text-white" dangerouslySetInnerHTML={{ __html: t.accessSwitching.other.title }}></h2>
              <p className="text-white opacity-75" style={{ fontSize: '1.2rem' }}>{t.accessSwitching.other.subtitle}</p>
            </Col>
            <Row className="g-4">
               {[
                  { d: t.accessSwitching.other.wifi, i: FaWifi, link: '/reseau/solutions-wifi' },
                  { d: t.accessSwitching.other.security, i: FaShieldAlt, link: '/reseau/securite-reseau' },
                  { d: t.accessSwitching.other.wan, i: FaNetworkWired, link: '/reseau/solutions-wan' }
               ].map((sol, i) => (
                  <Col lg={4} key={i}>
                    <motion.div whileHover={{ y: -12, scale: 1.02 }} onClick={() => window.location.href = sol.link} className="h-100 p-5 bg-white rounded-4 shadow-lg" style={{ cursor: 'pointer' }}>
                       <div className="mb-4 d-flex align-items-center justify-content-center" style={{ width: 70, height: 70, background: 'rgba(217,0,0,0.1)', borderRadius: 16 }}>
                         <sol.i color="#d90000" size={30} />
                       </div>
                       <h4 className="fw-bold mb-3">{sol.d.title}</h4>
                       <p className="text-muted small mb-4">{sol.d.desc}</p>
                       <div className="mt-3">
                         {sol.d.f.map((feat, idx) => <div key={idx} className="small d-flex align-items-start mb-2"><FaCheckCircle color="#d90000" className="me-2 mt-1" />{feat}</div>)}
                       </div>
                    </motion.div>
                  </Col>
               ))}
            </Row>
         </Container>
      </section>

      {/* CTA Section */}
      <section className="py-5" style={{ background: '#f8fafc' }}>
        <Container>
          <motion.div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', borderRadius: '30px', padding: '5rem 3rem', color: 'white', position: 'relative' }} className="text-center shadow-lg">
            <div style={{ background: 'rgba(217,0,0,0.2)', borderRadius: '20px', width: '80px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 2rem', border: '2px solid rgba(217,0,0,0.3)' }}><FaLightbulb size={35} color="#d90000" /></div>
            <h3 className="display-5 fw-bold mb-3">{t.accessSwitching.cta.title}</h3>
            <p className="lead opacity-75 mb-5 mx-auto" style={{ maxWidth: '750px' }}>{t.accessSwitching.cta.subtitle}</p>
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <Button variant="light" size="lg" onClick={() => window.location.href='/contact'} className="px-5 py-3 fw-bold rounded-pill"><FaPhone className="me-2" />{t.accessSwitching.cta.btnExpert}</Button>
              <Button variant="outline-light" size="lg" className="px-5 py-3 fw-bold rounded-pill border-2"><FaExternalLinkAlt className="me-2" />{t.accessSwitching.cta.btnCatalog}</Button>
            </div>
          </motion.div>
        </Container>
      </section>
    </div>
  );
};

export default AccessSwitching;